/**
 * PayerStatsSidebar
 *
 * The patients-list left rail: payer summary stats and coverage-bucket badges,
 * plus the cards other features contribute to the rail (eligibility checks,
 * requirements). Only rendered at md+ breakpoints — hidden on mobile.
 */

import React from 'react';
import { EligibilityChecksSummaryCard } from '@features/eligibility-checks/components/EligibilityChecksSummaryCard';
import { useGetPayerStatsQuery } from '@features/patient-coverages';
import { RequirementsRailCard } from '@features/patient-requirements';
import { useSubjectLanguage } from '@features/user';
import {
  ErrorOutlined as ErrorIcon,
  PaymentsOutlined as PaymentsIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import { Avatar, Box, Chip, Divider, Skeleton, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

import type { FilterClearSource, RequirementFilterTarget } from '../hooks/usePatientList';
import type { CoverageBucket, PatientListFilter } from '../types';

interface PayerStatsSidebarProps {
  activeFilter: PatientListFilter;
  onPayerSelect: (payerUuid: string, payerName: string) => void;
  onBucketSelect: (bucket: CoverageBucket) => void;
  /**
   * Absent until GDN-3423's parameter exists. The Requirements card renders
   * its rows as plain rows without it, rather than as buttons that filter
   * nothing.
   */
  onRequirementSelect?: (target: RequirementFilterTarget) => void;
  onClearFilter: (source?: FilterClearSource) => void;
  disabled?: boolean;
}

export const PayerStatsSidebar: React.FC<PayerStatsSidebarProps> = ({
  activeFilter,
  onPayerSelect,
  onBucketSelect,
  onRequirementSelect,
  onClearFilter,
  disabled = false,
}) => {
  const theme = useTheme();
  const subjectLanguage = useSubjectLanguage();
  const { data, isLoading } = useGetPayerStatsQuery();

  const isPayerActive = (payerUuid: string) =>
    activeFilter?.type === 'payer' && activeFilter.payer_uuid === payerUuid;

  const isBucketActive = (bucket: CoverageBucket) =>
    activeFilter?.type === 'coverage_bucket' && activeFilter.bucket === bucket;

  const handlePayerClick = (payerUuid: string, payerName: string) => {
    if (disabled) return;
    if (isPayerActive(payerUuid)) {
      onClearFilter();
    } else {
      onPayerSelect(payerUuid, payerName);
    }
  };

  const handleBucketClick = (bucket: CoverageBucket) => {
    if (disabled) return;
    if (isBucketActive(bucket)) {
      onClearFilter();
    } else {
      onBucketSelect(bucket);
    }
  };

  return (
    <Box
      sx={{
        width: 240,
        flexShrink: 0,
        display: { xs: 'none', md: 'block' },
      }}
    >
      <Box sx={{ position: 'sticky', top: 24 }}>
        <Box
          sx={{
            border: `1px solid ${theme.palette.grey[200]}`,
            borderRadius: 2,
            bgcolor: 'background.paper',
            overflow: 'hidden',
            opacity: disabled ? 0.5 : 1,
            pointerEvents: disabled ? 'none' : 'auto',
            transition: 'opacity 0.2s',
          }}
        >
          {/* Header */}
          <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.grey[100]}` }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, color: theme.palette.text.primary }}
            >
              Payers
            </Typography>
            {data && (
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                {data.total_patients.toLocaleString()}{' '}
                {data.total_patients === 1 ? subjectLanguage : `${subjectLanguage}s`}
              </Typography>
            )}
          </Box>

          {/* Payer list */}
          <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
            {isLoading ? (
              <Box sx={{ p: 1.5 }}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <Box
                    key={i}
                    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 0.5 }}
                  >
                    <Skeleton variant="circular" width={28} height={28} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton width="70%" height={16} />
                    </Box>
                    <Skeleton width={24} height={16} />
                  </Box>
                ))}
              </Box>
            ) : (
              <Box sx={{ py: 0.5 }}>
                {data?.payers.map(payer => {
                  const active = isPayerActive(payer.payer_uuid);
                  return (
                    <Tooltip key={payer.payer_uuid} title={payer.payer_name} placement="left" arrow>
                      <Box
                        onClick={() => handlePayerClick(payer.payer_uuid, payer.payer_name)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handlePayerClick(payer.payer_uuid, payer.payer_name);
                          }
                        }}
                        aria-pressed={active}
                        aria-label={`Filter by ${payer.payer_name}, ${payer.patient_count} ${subjectLanguage}s`}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          px: 2,
                          py: 1,
                          cursor: 'pointer',
                          bgcolor: active ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                          borderLeft: active
                            ? `3px solid ${theme.palette.primary.main}`
                            : '3px solid transparent',
                          '&:hover': {
                            bgcolor: active
                              ? alpha(theme.palette.primary.main, 0.12)
                              : theme.palette.action.hover,
                          },
                          transition: 'background-color 0.15s, border-color 0.15s',
                        }}
                      >
                        <Avatar
                          src={payer.avatar_url ?? undefined}
                          sx={{
                            width: 28,
                            height: 28,
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            bgcolor: active ? theme.palette.primary.main : theme.palette.grey[300],
                            color: active
                              ? theme.palette.primary.contrastText
                              : theme.palette.text.primary,
                          }}
                        >
                          {payer.payer_name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography
                          variant="body2"
                          sx={{
                            flex: 1,
                            fontWeight: active ? 600 : 400,
                            color: active ? theme.palette.primary.main : theme.palette.text.primary,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {payer.payer_name}
                        </Typography>
                        <Chip
                          label={payer.patient_count}
                          size="small"
                          sx={{
                            height: 20,
                            minWidth: 28,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            bgcolor: active
                              ? alpha(theme.palette.primary.main, 0.15)
                              : theme.palette.grey[100],
                            color: active
                              ? theme.palette.primary.main
                              : theme.palette.text.secondary,
                            '& .MuiChip-label': { px: 0.75 },
                          }}
                        />
                      </Box>
                    </Tooltip>
                  );
                })}

                {data?.payers.length === 0 && (
                  <Typography
                    variant="body2"
                    sx={{ px: 2, py: 2, color: theme.palette.text.secondary, textAlign: 'center' }}
                  >
                    No payers found
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* Buckets divider & badges */}
          {!isLoading &&
            data &&
            (data.self_pay_count > 0 ||
              data.needs_review_count > 0 ||
              data.no_coverage_count > 0) && (
              <>
                <Divider />
                <Box sx={{ py: 0.5 }}>
                  {data.self_pay_count > 0 && (
                    <BucketRow
                      icon={<PaymentsIcon sx={{ fontSize: 18, color: theme.palette.info.main }} />}
                      label="Self-pay"
                      count={data.self_pay_count}
                      active={isBucketActive('self_pay')}
                      color={theme.palette.info.main}
                      subjectLanguage={subjectLanguage}
                      onClick={() => handleBucketClick('self_pay')}
                    />
                  )}
                  {data.needs_review_count > 0 && (
                    <BucketRow
                      icon={
                        <WarningIcon sx={{ fontSize: 18, color: theme.palette.warning.main }} />
                      }
                      label="Needs Review"
                      count={data.needs_review_count}
                      active={isBucketActive('needs_review')}
                      color={theme.palette.warning.main}
                      subjectLanguage={subjectLanguage}
                      onClick={() => handleBucketClick('needs_review')}
                    />
                  )}
                  {data.no_coverage_count > 0 && (
                    <BucketRow
                      icon={<ErrorIcon sx={{ fontSize: 18, color: theme.palette.error.main }} />}
                      label="No Coverage"
                      count={data.no_coverage_count}
                      active={isBucketActive('no_coverage')}
                      color={theme.palette.error.main}
                      subjectLanguage={subjectLanguage}
                      onClick={() => handleBucketClick('no_coverage')}
                    />
                  )}
                </Box>
              </>
            )}
        </Box>

        {/* The rail hosts cards other features own — the eligibility summary
            already did. Requirements (AC-23) renders nothing unless the org is
            behind the gate, the user can manage billing, and the catalog holds
            an active definition. It takes `disabled` like the payer card: a
            search and a requirement filter cannot both apply, so while a
            search is active its rows stop offering the click rather than
            offering one the list will not honour. */}
        <RequirementsRailCard
          activeDefinitionId={
            activeFilter?.type === 'requirement'
              ? activeFilter.patient_requirement_definition_id
              : null
          }
          onSelect={onRequirementSelect}
          onClear={() => onClearFilter('rail_row')}
          disabled={disabled}
        />

        <EligibilityChecksSummaryCard />
      </Box>
    </Box>
  );
};

// ── Internal sub-component ─────────────────────────────────────

interface BucketRowProps {
  icon: React.ReactNode;
  label: string;
  count: number;
  active: boolean;
  color: string;
  subjectLanguage: string;
  onClick: () => void;
}

const BucketRow: React.FC<BucketRowProps> = ({
  icon,
  label,
  count,
  active,
  color,
  subjectLanguage,
  onClick,
}) => {
  const theme = useTheme();
  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      aria-pressed={active}
      aria-label={`Filter by ${label}, ${count} ${subjectLanguage}s`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1,
        cursor: 'pointer',
        bgcolor: active ? alpha(color, 0.08) : 'transparent',
        borderLeft: active ? `3px solid ${color}` : '3px solid transparent',
        '&:hover': {
          bgcolor: active ? alpha(color, 0.12) : theme.palette.action.hover,
        },
        transition: 'background-color 0.15s, border-color 0.15s',
      }}
    >
      {icon}
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          fontWeight: active ? 600 : 400,
          color: active ? color : theme.palette.text.primary,
        }}
      >
        {label}
      </Typography>
      <Chip
        label={count}
        size="small"
        sx={{
          height: 20,
          minWidth: 28,
          fontSize: '0.7rem',
          fontWeight: 600,
          bgcolor: active ? alpha(color, 0.15) : theme.palette.grey[100],
          color: active ? color : theme.palette.text.secondary,
          '& .MuiChip-label': { px: 0.75 },
        }}
      />
    </Box>
  );
};
